import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { css, cx } from "styled-system/css";
import { Pencil, X, ChevronDown, ChevronRight } from "lucide-react";
import { api, type ApiResponse } from "~/lib/api";
import { ak, Modal, PageHead, Section, Status, Tag } from "~/components/editorial";
import type { Tool, JiraTeam, ToolJiraProjectUrl } from "./types";
import { AUTH_METHOD_LABELS } from "./types";

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "–";
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Days from today to the renewal date; negative when overdue. */
function daysUntil(dateStr: string): number {
  const ms = new Date(dateStr).getTime() - Date.now();
  return Math.ceil(ms / 86_400_000);
}

function RenewalStatus({ renewalDate }: { renewalDate: string }) {
  const days = daysUntil(renewalDate);
  if (days < 0) {
    return (
      <Status kind="fail">
        Overdue by <span className={ak.mono}>{-days}</span>{" "}
        {-days === 1 ? "day" : "days"}
      </Status>
    );
  }
  if (days <= 30) {
    return (
      <Status kind="wait">
        Renews in <span className={ak.mono}>{days}</span>{" "}
        {days === 1 ? "day" : "days"}
      </Status>
    );
  }
  return (
    <Status kind="ok">
      <span className={ak.mono}>{days}</span> days out
    </Status>
  );
}

export function ToolDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editingJiraTeam, setEditingJiraTeam] = useState<JiraTeam | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["tool", id],
    queryFn: () => api.get<ApiResponse<Tool>>(`/tools/${id}`),
    enabled: !!id,
  });
  const tool = data?.data;

  const handleDelete = async () => {
    if (!tool) return;
    if (!confirm(`Delete "${tool.name}"? This cannot be undone.`)) return;
    await api.delete(`/tools/${tool.id}`);
    queryClient.invalidateQueries({ queryKey: ["tools"] });
    navigate("/tools");
  };

  if (isLoading) {
    return (
      <div className={ak.page}>
        <div className={ak.loading}>Loading…</div>
      </div>
    );
  }

  if (!tool) {
    return (
      <div className={ak.page}>
        <div className={ak.empty}>
          <p>Tool not found.</p>
          <p>
            <Link to="/tools" className={ak.link}>
              Back to the tools tracker
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={ak.page}>
      <PageHead
        back={{ to: "/tools", label: "Tools" }}
        kicker={
          <>
            Admin · Tools tracker
            {tool.authMethod && (
              <> · {AUTH_METHOD_LABELS[tool.authMethod] ?? tool.authMethod}</>
            )}
          </>
        }
        title={tool.name}
        dek={tool.description ?? undefined}
        actions={
          <>
            <Link to={`/tools/${tool.id}/edit`} className={ak.btnSecondary}>
              Edit
            </Link>
            <button onClick={handleDelete} className={ak.btnDanger}>
              Delete
            </button>
          </>
        }
      />

      <div className={styles.twoUp}>
        <Section kicker="I. Record" title="Access">
          <dl className={ak.dl}>
            <dt>URL</dt>
            <dd>
              {tool.url ? (
                <a
                  href={tool.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cx(ak.link, ak.mono, styles.small)}
                >
                  {tool.url}
                </a>
              ) : (
                <span className={ak.muted}>–</span>
              )}
            </dd>
            <dt>Auth method</dt>
            <dd>
              {tool.authMethod ? (
                AUTH_METHOD_LABELS[tool.authMethod] ?? tool.authMethod
              ) : (
                <span className={ak.muted}>–</span>
              )}
            </dd>
            {tool.accessProvider && (
              <>
                <dt>Getting access</dt>
                <dd className={styles.pre}>{tool.accessProvider}</dd>
              </>
            )}
            <dt>Who should have it</dt>
            <dd>
              {tool.accessRoles.length > 0 ? (
                <div className={ak.tagRow}>
                  {tool.accessRoles.map(({ accessRole }) => (
                    <Tag key={accessRole.id} name={accessRole.name} />
                  ))}
                </div>
              ) : (
                <span className={ak.muted}>–</span>
              )}
            </dd>
            {tool.labels.length > 0 && (
              <>
                <dt>Labels</dt>
                <dd>
                  <div className={ak.tagRow}>
                    {tool.labels.map(({ label }) => (
                      <Tag
                        key={label.id}
                        name={label.name}
                        color={label.color}
                      />
                    ))}
                  </div>
                </dd>
              </>
            )}
            {tool.notes && (
              <>
                <dt>Notes</dt>
                <dd className={styles.pre}>{tool.notes}</dd>
              </>
            )}
          </dl>
        </Section>

        <Section kicker="II. Renewal" title="Billing">
          <dl className={cx(ak.dl, styles.dlNarrow)}>
            <dt>Paid tool</dt>
            <dd>{tool.isPaid ? "Yes" : "No"}</dd>
            <dt>Renewal date</dt>
            <dd className={ak.mono}>
              {tool.renewalDate ? (
                formatDate(tool.renewalDate)
              ) : (
                <span className={ak.muted}>Not set</span>
              )}
            </dd>
            {tool.renewalDate && (
              <>
                <dt>Status</dt>
                <dd>
                  <RenewalStatus renewalDate={tool.renewalDate} />
                </dd>
              </>
            )}
            {tool.renewalNotes && (
              <>
                <dt>Renewal notes</dt>
                <dd className={styles.pre}>{tool.renewalNotes}</dd>
              </>
            )}
          </dl>
        </Section>
      </div>

      <Section
        kicker="III. Teams"
        title={
          tool.jiraTeams.length === 0
            ? "No team uses it yet"
            : `${tool.jiraTeams.length} ${tool.jiraTeams.length === 1 ? "team uses" : "teams use"} it`
        }
        actions={
          <Link
            to={`/tools/${tool.id}/edit`}
            className={cx(ak.btnSecondary, ak.btnSm)}
          >
            Manage teams
          </Link>
        }
      >
        {tool.jiraTeams.length === 0 ? (
          <div className={ak.empty}>
            <p>No teams assigned.</p>
            <p>
              <Link to={`/tools/${tool.id}/edit`} className={ak.link}>
                Add teams from the edit page.
              </Link>
            </p>
          </div>
        ) : (
          <div className={ak.list}>
            {tool.jiraTeams.map((jiraTeam) => (
              <JiraTeamRow
                key={jiraTeam.id}
                toolId={tool.id}
                jiraTeam={jiraTeam}
                onEditNotes={() => setEditingJiraTeam(jiraTeam)}
                onUrlsChanged={refetch}
              />
            ))}
          </div>
        )}
        <p className={ak.note}>
          Teams are Jira projects. Each team can keep its own links, like its
          Confluence space, and notes on how it uses the tool.
        </p>
      </Section>

      {editingJiraTeam && (
        <EditJiraTeamNotesModal
          toolId={tool.id}
          jiraTeam={editingJiraTeam}
          onClose={() => setEditingJiraTeam(null)}
          onSaved={() => {
            setEditingJiraTeam(null);
            refetch();
          }}
        />
      )}
    </div>
  );
}

// ─── JiraTeamRow ──────────────────────────────────────────────────────────────

function JiraTeamRow({
  toolId,
  jiraTeam,
  onEditNotes,
  onUrlsChanged,
}: {
  toolId: string;
  jiraTeam: JiraTeam;
  onEditNotes: () => void;
  onUrlsChanged: () => void;
}) {
  const [showAddUrl, setShowAddUrl] = useState(false);
  const [urlsOpen, setUrlsOpen] = useState(false);

  const urlCount = jiraTeam.urls.length;
  const showAccordion = urlCount > 0 || showAddUrl;

  return (
    <div className={styles.teamRow}>
      <div className={styles.teamHead}>
        <div className={styles.teamName}>
          {jiraTeam.jiraProject.avatarUrl && (
            <img
              src={jiraTeam.jiraProject.avatarUrl}
              alt=""
              className={cx(ak.avatar, styles.projectAvatar)}
            />
          )}
          <span className={styles.teamNameText}>
            {jiraTeam.jiraProject.name}
          </span>
          <span className={cx(ak.mono, ak.muted, styles.small)}>
            {jiraTeam.jiraProject.key}
          </span>
          {urlCount > 0 && (
            <button
              onClick={() => setUrlsOpen((v) => !v)}
              className={ak.btnText}
              aria-expanded={urlsOpen}
            >
              {urlsOpen ? (
                <ChevronDown size={12} />
              ) : (
                <ChevronRight size={12} />
              )}
              <span className={ak.mono}>{urlCount}</span> link
              {urlCount !== 1 ? "s" : ""}
            </button>
          )}
        </div>
        <div className={ak.actions}>
          <button
            onClick={() => {
              setShowAddUrl(true);
              setUrlsOpen(true);
            }}
            className={cx(ak.btnSecondary, ak.btnSm)}
          >
            Add link
          </button>
          <button
            onClick={onEditNotes}
            className={cx(ak.btnSecondary, ak.btnSm)}
          >
            Notes
          </button>
        </div>
      </div>

      {jiraTeam.notes && <p className={styles.teamNotes}>{jiraTeam.notes}</p>}

      {showAccordion && urlsOpen && (
        <div className={styles.urlList}>
          {jiraTeam.urls.map((u) => (
            <UrlItem
              key={u.id}
              toolId={toolId}
              jiraProjectId={jiraTeam.jiraProjectId}
              url={u}
              onChanged={onUrlsChanged}
            />
          ))}
          {showAddUrl && (
            <AddUrlInline
              toolId={toolId}
              jiraProjectId={jiraTeam.jiraProjectId}
              onCancel={() => setShowAddUrl(false)}
              onAdded={() => {
                setShowAddUrl(false);
                onUrlsChanged();
              }}
            />
          )}
        </div>
      )}

      {showAddUrl && !urlsOpen && (
        <div className={styles.urlList}>
          <AddUrlInline
            toolId={toolId}
            jiraProjectId={jiraTeam.jiraProjectId}
            onCancel={() => setShowAddUrl(false)}
            onAdded={() => {
              setShowAddUrl(false);
              onUrlsChanged();
            }}
          />
        </div>
      )}
    </div>
  );
}

// ─── UrlItem ──────────────────────────────────────────────────────────────────

function UrlItem({
  toolId,
  jiraProjectId,
  url,
  onChanged,
}: {
  toolId: string;
  jiraProjectId: string;
  url: ToolJiraProjectUrl;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(url.name);
  const [urlVal, setUrlVal] = useState(url.url);
  const [notes, setNotes] = useState(url.notes ?? "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !urlVal) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await api.patch(
        `/tools/${toolId}/jira-teams/${jiraProjectId}/urls/${url.id}`,
        {
          name,
          url: urlVal,
          notes: notes || null,
        },
      );
      setEditing(false);
      onChanged();
    } catch (err) {
      console.error(err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to save. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Delete this URL?")) return;
    await api.delete(
      `/tools/${toolId}/jira-teams/${jiraProjectId}/urls/${url.id}`,
    );
    onChanged();
  };

  const handleCancel = () => {
    setName(url.name);
    setUrlVal(url.url);
    setNotes(url.notes ?? "");
    setEditing(false);
  };

  if (editing) {
    return (
      <UrlForm
        onSubmit={handleSave}
        name={name}
        setName={setName}
        url={urlVal}
        setUrl={setUrlVal}
        notes={notes}
        setNotes={setNotes}
        error={error}
        onCancel={handleCancel}
        submitLabel={isSubmitting ? "Saving…" : "Save"}
        submitDisabled={!name || !urlVal || isSubmitting}
        autoFocus
      />
    );
  }

  return (
    <div className={styles.urlItem}>
      <div className={styles.urlMain}>
        <span className={styles.urlName}>{url.name}</span>
        <a
          href={url.url}
          target="_blank"
          rel="noopener noreferrer"
          className={cx(ak.link, ak.mono, styles.urlHref)}
        >
          {url.url} ↗
        </a>
        {url.notes && <p className={styles.urlNotes}>{url.notes}</p>}
      </div>
      <div className={ak.actions}>
        <button
          onClick={() => setEditing(true)}
          className={ak.iconBtn}
          title="Edit link"
          aria-label="Edit link"
        >
          <Pencil size={12} />
        </button>
        <button
          onClick={handleDelete}
          className={cx(ak.iconBtn, ak.iconBtnDanger)}
          title="Remove link"
          aria-label="Remove link"
        >
          <X size={12} />
        </button>
      </div>
    </div>
  );
}

// ─── UrlForm (shared by add and edit) ─────────────────────────────────────────

function UrlForm({
  onSubmit,
  name,
  setName,
  url,
  setUrl,
  notes,
  setNotes,
  error,
  onCancel,
  submitLabel,
  submitDisabled,
  autoFocus,
}: {
  onSubmit: (e: React.FormEvent) => void;
  name: string;
  setName: (v: string) => void;
  url: string;
  setUrl: (v: string) => void;
  notes: string;
  setNotes: (v: string) => void;
  error: string | null;
  onCancel: () => void;
  submitLabel: string;
  submitDisabled: boolean;
  autoFocus?: boolean;
}) {
  return (
    <form onSubmit={onSubmit} className={styles.urlForm}>
      <div className={styles.urlFields}>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name or description"
          aria-label="Link name"
          required
          className={cx(ak.input, styles.inputSm)}
          autoFocus={autoFocus}
        />
        <input
          type="text"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://…"
          aria-label="Link URL"
          required
          className={cx(ak.input, ak.mono, styles.inputSm)}
        />
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes (optional)"
          aria-label="Link notes"
          className={cx(ak.input, styles.inputSm)}
        />
      </div>
      {error && (
        <Status kind="fail" className={styles.wrap}>
          {error}
        </Status>
      )}
      <div className={ak.actions}>
        <button
          type="button"
          onClick={onCancel}
          className={cx(ak.btnSecondary, ak.btnSm)}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitDisabled}
          className={cx(ak.btn, ak.btnSm)}
        >
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

// ─── AddUrlInline ─────────────────────────────────────────────────────────────

function AddUrlInline({
  toolId,
  jiraProjectId,
  onCancel,
  onAdded,
}: {
  toolId: string;
  jiraProjectId: string;
  onCancel: () => void;
  onAdded: () => void;
}) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !url) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await api.post(`/tools/${toolId}/jira-teams/${jiraProjectId}/urls`, {
        name,
        url,
        notes: notes || undefined,
      });
      onAdded();
    } catch (err) {
      console.error(err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to save URL. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <UrlForm
      onSubmit={handleSubmit}
      name={name}
      setName={setName}
      url={url}
      setUrl={setUrl}
      notes={notes}
      setNotes={setNotes}
      error={error}
      onCancel={onCancel}
      submitLabel={isSubmitting ? "Adding…" : "Add link"}
      submitDisabled={!name || !url || isSubmitting}
    />
  );
}

// ─── EditJiraTeamNotesModal ───────────────────────────────────────────────────

function EditJiraTeamNotesModal({
  toolId,
  jiraTeam,
  onClose,
  onSaved,
}: {
  toolId: string;
  jiraTeam: JiraTeam;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [notes, setNotes] = useState(jiraTeam.notes ?? "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.patch(`/tools/${toolId}/jira-teams/${jiraTeam.jiraProjectId}`, {
        notes: notes || null,
      });
      onSaved();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal title={`${jiraTeam.jiraProject.name}: notes`} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <div className={ak.field}>
          <label className={ak.label} htmlFor="team-notes">
            Notes
          </label>
          <textarea
            id="team-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            placeholder="How this team uses the tool…"
            className={ak.textarea}
          />
        </div>
        <div className={ak.modalActions}>
          <button type="button" onClick={onClose} className={ak.btnSecondary}>
            Cancel
          </button>
          <button type="submit" disabled={isSubmitting} className={ak.btn}>
            {isSubmitting ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

const styles = {
  twoUp: css({
    display: "grid",
    gridTemplateColumns: { base: "1fr", lg: "minmax(0, 3fr) minmax(0, 2fr)" },
    gap: "0 28px",
    alignItems: "start",
  }),
  dlNarrow: css({
    gridTemplateColumns: { base: "1fr!", sm: "130px minmax(0, 1fr)!" },
  }),
  pre: css({ whiteSpace: "pre-wrap" }),
  small: css({ fontSize: "12px" }),
  wrap: css({ whiteSpace: "normal" }),
  teamRow: css({
    py: "10px",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "ed.rule",
  }),
  teamHead: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
  }),
  teamName: css({
    display: "flex",
    alignItems: "baseline",
    flexWrap: "wrap",
    gap: "4px 10px",
    minWidth: 0,
  }),
  projectAvatar: css({
    width: "16px!",
    height: "16px!",
    alignSelf: "center",
  }),
  teamNameText: css({
    fontFamily: "serif",
    fontSize: "16px",
    fontWeight: 700,
  }),
  teamNotes: css({
    m: 0,
    mt: "4px",
    fontSize: "13px",
    lineHeight: 1.45,
    color: "ed.ink2",
    whiteSpace: "pre-wrap",
  }),
  urlList: css({
    mt: "8px",
    ml: { base: 0, sm: "16px" },
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderColor: "ed.grid",
  }),
  urlItem: css({
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
    py: "6px",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "ed.grid",
  }),
  urlMain: css({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "baseline",
    gap: "2px 10px",
    minWidth: 0,
  }),
  urlName: css({ fontSize: "13px", fontWeight: 600 }),
  urlHref: css({ fontSize: "11.5px", overflowWrap: "anywhere" }),
  urlNotes: css({
    flexBasis: "100%",
    m: 0,
    fontSize: "12px",
    color: "ed.ink3",
  }),
  urlForm: css({
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    py: "8px",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "ed.grid",
  }),
  urlFields: css({
    display: "grid",
    gridTemplateColumns: { base: "1fr", md: "1fr 1.4fr 1fr" },
    gap: "6px",
  }),
  inputSm: css({ py: "5px!", fontSize: "13px!" }),
};
